<template>
  <div
    ref="containerRef"
    class="package-cover-card-container"
    @click="handleClick"
  >
    <div class="package-cover-card">
      <div class="package-cover-card__toolbar">
        <div class="package-cover-card__toolbar__group">
          <!-- 还没有当日学习计划功能 
          <TagButton
            v-if="cardCountToday > 0"
            variant="tag"
            icon="icon_v2/mono/calendar"
            :label="`${cardCountToday}`"
            :size="BASE_TAG_SIZE * scale"
          />
          -->
          <!-- 还没有收藏功能
          <TagButton
            variant="tag"
            icon="icon_v2/mono/star"
            :size="BASE_TAG_SIZE * scale"
          />
          -->
        </div>
        <div class="package-cover-card__toolbar__group">
          <Tag
            v-if="isTrial"
            :label="_t('package.trailing')"
            :size="BASE_TAG_SIZE * scale"
          />
          <Tag
            v-if="showShare && ownPackage"
            :label="hasUnpublished ? _t('package.changed') : ''"
            icon="icon_v2/mono/user-1"
            :size="BASE_TAG_SIZE * scale"
            @click.stop="emit('shareClick', $event)"
          />
          <Tag
            v-if="showMenu"
            icon="icon_v2/mono/menu-1"
            :size="BASE_TAG_SIZE * scale"
            @click.stop="emit('menuClick', $event)"
          />
        </div>
      </div>
      <PackageCover
        :packageStyle="package.style"
        :size="BASE_COVER_SIZE * scale"
      />
      <div class="package-cover-card__progress">
        <svg
          class="package-cover-card__progress__bg"
          xmlns="http://www.w3.org/2000/svg"
          :width="158 * scale"
          :height="30 * scale"
          :viewBox="`0 0 ${158 * scale} ${30 * scale}`"
          fill="none"
        >
          <path
            :d="scaledProgressSvgPath"
            fill="white"
          />
        </svg>
        <ProgressBar
          ref="progressBarRef"
          class="package-cover-card__progress__bar"
          :value="package.cardCountLearned ?? 0"
          :max="package.cardCount"
          color="green"
          background="neutral"
          :width="123 * scale"
          :height="15 * scale"
        ></ProgressBar>
      </div>
      <div class="package-cover-card__title">
        <span
          ref="titleText"
          class="package-cover-card__title__text"
        >
          {{ package.name }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Tag from '@/components/basic/trivial/Tag.vue'
import ProgressBar from '@/components/basic/trivial/ProgressBar.vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { PackageAccess, type PackageBasic } from '@/api/package-source'
import PackageCover from '@/components/business/package/PackageCover.vue'
import { isPackageUpdateUnreleased } from '@/utils/package'
import { useDebounceClick } from '@/composables/debounceClick'

const props = withDefaults(
  defineProps<{
    package: PackageBasic
    showShare?: boolean
    showMenu?: boolean
    width?: number
    height?: number
  }>(),
  {
    showShare: true,
    showMenu: true,
    width: undefined,
    height: undefined,
  }
)

const emit = defineEmits<{
  click: [MouseEvent]
  shareClick: [MouseEvent]
  menuClick: [MouseEvent]
}>()

// 使用防抖点击
const { handleClick } = useDebounceClick((event: MouseEvent) => {
  emit('click', event)
})

// 试学中
const isTrial = computed(() => props.package.packageAccess == null)
// 拥有卡包
const ownPackage = computed(
  () => props.package.packageAccess === PackageAccess.Author
)
// 有修改
const hasUnpublished = computed(() => isPackageUpdateUnreleased(props.package))

const containerRef = ref<HTMLElement | null>(null)
const titleText = ref(null)
const progressBarRef = ref<any>(null)

// 基础尺寸
const BASE_COVER_SIZE = 232
const BASE_TITLE_HEIGHT = 56
const BASE_WIDTH = BASE_COVER_SIZE
const BASE_HEIGHT = BASE_COVER_SIZE + BASE_TITLE_HEIGHT
const BASE_TAG_SIZE = 28

// 基于props计算缩放比例
const scale = computed(() => {
  const hasWidth = props.width !== undefined
  const hasHeight = props.height !== undefined

  // 都没有传入，使用默认比例 1
  if (!hasWidth && !hasHeight) {
    return 1
  }

  // 只传入宽度，基于宽度计算
  if (hasWidth && !hasHeight) {
    return props.width! / BASE_WIDTH
  }

  // 只传入高度，基于高度计算
  if (!hasWidth && hasHeight) {
    return props.height! / BASE_HEIGHT
  }

  // 都传入，取较小的比例以保持比例
  const scaleX = props.width! / BASE_WIDTH
  const scaleY = props.height! / BASE_HEIGHT
  return Math.min(scaleX, scaleY)
})

// 计算缩放后的SVG路径
const scaledProgressSvgPath = computed(() => {
  const s = scale.value
  return `M${5.74824 * s} ${15.0854 * s}L${0.647949 * s} ${30.6213 * s}H${
    157.648 * s
  }L${152.548 * s} ${15.0854 * s}C${149.72 * s} ${6.57612 * s} ${142.68 * s} ${
    0.967743 * s
  } ${134.863 * s} ${0.967743 * s}H${23.4329 * s}C${15.6162 * s} ${
    0.967743 * s
  } ${8.57557 * s} ${6.57612 * s} ${5.74824 * s} ${15.0854 * s}Z`
})

// ResizeObserver 来监听容器尺寸变化
let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  if (titleText.value) {
    const element = titleText.value as HTMLElement
    const lineHeight = parseInt(window.getComputedStyle(element).lineHeight)
    const height = element.offsetHeight
    if (height > lineHeight * 1.5) {
      element.classList.add('is-twoline')
    }
  }

  if (containerRef.value) {
    // 监听尺寸变化，触发ProgressBar重新计算宽度
    resizeObserver = new ResizeObserver(() => {
      // 触发ProgressBar重新计算宽度
      if (progressBarRef.value && progressBarRef.value.$el) {
        // 触发resize事件让ProgressBar重新计算容器宽度
        window.dispatchEvent(new Event('resize'))
      }
    })
    resizeObserver.observe(containerRef.value)
  }
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})
</script>

<style scoped>
.package-cover-card-container {
  /* 外层容器，接收外部的width/height设置 */
  display: flex;
  align-items: center;
  justify-content: center;
  /* 确保外阴影能看到 */
  overflow: visible;
}

.package-cover-card {
  position: relative;
  display: flex;
  width: calc(232px * v-bind(scale));
  height: calc(288px * v-bind(scale));
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  border-radius: calc(20px * v-bind(scale));
  background: #fff;
  box-shadow: var(--ld2-button-shadow-netural-large);
  flex-shrink: 0;
}

.package-cover-card__toolbar {
  position: absolute;
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: calc(var(--ld2-spacing-3) * v-bind(scale));
}

.package-cover-card__toolbar__group {
  display: flex;
  gap: calc(var(--ld2-spacing-3) * v-bind(scale));
}

.package-cover-card__progress {
  position: absolute;
  bottom: calc(calc(56px * v-bind(scale)));
  height: calc(30px * v-bind(scale));
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.package-cover-card__progress__bg {
  position: absolute;
  bottom: 0;
  height: calc(30px * v-bind(scale));
  z-index: 0;
}

.package-cover-card__progress__bar {
  position: absolute;
  bottom: calc(5px * v-bind(scale));
  z-index: 1;
  font-size: calc(15px * v-bind(scale)) !important;
  line-height: calc(15px * v-bind(scale)) !important;
}

.package-cover-card__title {
  display: flex;
  height: calc(56px * v-bind(scale));
  width: 100%;
  padding: 0 calc(12px * v-bind(scale));
  align-items: center;
  justify-content: center;
  font: var(--ld2-bodylabel-large-b);
  color: var(--ld2-label-primary);
}

.package-cover-card__title__text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  font-size: calc(19px * v-bind(scale));
}

.package-cover-card__title__text.is-twoline {
  font-size: calc(19px * 0.9 * v-bind(scale));
}
</style>
